import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus, faTrash, faPencil, faCheck, faXmark,
  faGripVertical, faBuilding, faPerson, faArrowUpRightFromSquare,
  faLink, faEnvelope, faPhone, faBriefcase, faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { Modal } from "react-bootstrap";
import pipelineApi from "@serie3/freelancesplaceapi/api/crm/pipeline";
import CustomerModal from "../Shared/CustomerModal";
import AiResearchButton from "../Shared/AiResearchButton";

const getContactLink = (entry) => {
  const contact = entry.person ?? entry.kunde;
  if (!contact) return null;
  const sm = contact.socialmedia?.find((s) => s.value?.startsWith("http"));
  if (sm?.value) return sm.value;
  return contact.website || contact.linkedin_url || null;
};

const getContactName = (entry) => {
  if (entry.person) {
    const p = entry.person;
    return [p.vorname, p.name].filter(Boolean).join(" ") || "—";
  }
  if (entry.kunde) return entry.kunde.firmenname || "—";
  return "—";
};

const getContactDetails = (entry) => {
  const contact = entry.person ?? entry.kunde;
  if (!contact) return { rows: [], links: [] };

  const rows = [];
  const links = [];

  const position = contact.position || contact.beruf;
  if (position) rows.push({ icon: faBriefcase, text: position, href: null });

  // kontaktdaten holds typed entries: Telefon, Email, Mobil, etc.
  (contact.kontaktdaten || []).forEach((kd) => {
    const key = (kd.key_name || "").toLowerCase();
    const val = kd.value;
    if (!val) return;
    if (key.includes("mail")) {
      rows.push({ icon: faEnvelope, text: val, href: `mailto:${val}` });
    } else if (key.includes("telefon") || key.includes("phone") || key.includes("mobil") || key.includes("handy")) {
      rows.push({ icon: faPhone, text: val, href: `tel:${val}` });
    }
  });

  const city = contact.ort || contact.city;
  if (city) rows.push({ icon: faLocationDot, text: city, href: null });

  // soziale_medien holds social/website URLs
  const seen = new Set();
  (contact.soziale_medien || []).forEach((sm) => {
    const url = sm?.value;
    if (url && url.startsWith("http") && !seen.has(url)) {
      seen.add(url);
      links.push({ url, label: sm.key_name || "Link" });
    }
  });

  if (contact.website && !seen.has(contact.website)) {
    links.push({ url: contact.website, label: "Website" });
  }

  return { rows, links };
};

const scoreColor = (score) => {
  if (score == null) return { color: "#9e9e9e", bg: "#f5f5f5" };
  if (score >= 70)   return { color: "#2e7d32", bg: "#e8f5e9" };
  if (score >= 40)   return { color: "#e65100", bg: "#fff3e0" };
  return               { color: "#c62828", bg: "#ffebee" };
};

const EntryCard = ({ entry, onDelete, onDragStart, onEdit }) => {
  const isPerson = !!entry.person;
  const sc       = scoreColor(entry.score);
  const { rows, links } = getContactDetails(entry);
  const hasDetails = rows.length > 0 || links.length > 0;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, entry.id)}
      onClick={() => onEdit(entry)}
      style={{
        background: "#fff",
        borderRadius: "10px",
        padding: "10px 12px",
        marginBottom: "8px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        border: "1px solid #e8e8e8",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {/* Top row: icon · name · score · delete */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{
          width: "26px", height: "26px", borderRadius: "50%",
          background: isPerson ? "#e8f0fe" : "#e8f5e9",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <FontAwesomeIcon
            icon={isPerson ? faPerson : faBuilding}
            style={{ fontSize: "0.72rem", color: isPerson ? "#1a73e8" : "#2e7d32" }}
          />
        </span>
        <span style={{ flex: 1, fontSize: "0.88rem", fontWeight: 700, color: "#212121", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {getContactName(entry)}
        </span>
        {entry.score != null && (
          <span style={{ fontSize: "0.7rem", fontWeight: 700, color: sc.color, background: sc.bg, borderRadius: "12px", padding: "2px 7px", flexShrink: 0 }}>
            {entry.score}
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(entry.id); }}
          style={{ background: "none", border: "none", color: "#bdbdbd", cursor: "pointer", padding: "2px 3px", lineHeight: 1, flexShrink: 0 }}
        >
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: "0.72rem" }} />
        </button>
      </div>

      {/* Contact details */}
      {hasDetails && (
        <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #f5f5f5", display: "flex", flexDirection: "column", gap: "4px" }}>
          {rows.map(({ icon, text, href }, i) => {
            const inner = (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden" }}>
                <FontAwesomeIcon icon={icon} style={{ fontSize: "0.65rem", color: "#bdbdbd", flexShrink: 0, width: "10px" }} />
                <span style={{ fontSize: "0.75rem", color: href ? "#1a73e8" : "#757575", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: href ? 500 : 400 }}>
                  {text}
                </span>
              </div>
            );
            return href ? (
              <a key={i} href={href} onClick={(e) => e.stopPropagation()} style={{ textDecoration: "none" }}>
                {inner}
              </a>
            ) : inner;
          })}

          {/* Social media / website chips */}
          {links.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "2px" }}>
              {links.map(({ url, label }, i) => (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "4px",
                    padding: "2px 8px", borderRadius: "10px",
                    background: "#f0f4ff", border: "1px solid #d0deff",
                    color: "#1a73e8", fontSize: "0.7rem", fontWeight: 600,
                    textDecoration: "none", whiteSpace: "nowrap",
                  }}
                >
                  <FontAwesomeIcon icon={faLink} style={{ fontSize: "0.6rem" }} />
                  {label}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EntryEditModal = ({ entry, steps, onClose, onSave }) => {
  const isPerson  = !!entry?.person;
  const contact   = entry?.person ?? entry?.kunde;
  const [stepId, setStepId] = useState(entry?.step?.id ?? "");
  const [score, setScore]   = useState(entry?.score ?? 50);
  const [reason, setReason] = useState(entry?.reason ?? "");
  const [saving, setSaving] = useState(false);
  const navigate  = useNavigate();
  const contactId = contact?.id;
  const editPath  = isPerson ? `/crm/people/edit/${contactId}` : `/crm/companies/edit/${contactId}`;
  const sc        = scoreColor(score);

  const handleSave = async () => {
    setSaving(true);
    await onSave(entry, Number(stepId), score, reason);
    setSaving(false);
    onClose();
  };

  if (!entry) return null;

  return (
    <Modal show onHide={onClose} centered contentClassName="modal-pipeline-content">
      <div style={{ background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: "14px 14px 0 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ width: "32px", height: "32px", borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FontAwesomeIcon icon={isPerson ? faPerson : faBuilding} style={{ color: "#fff", fontSize: "0.85rem" }} />
          </span>
          <span style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>{getContactName(entry)}</span>
        </div>
        <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px", width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "1rem" }}>✕</button>
      </div>

      <div style={{ padding: "24px", background: "#f8f9fb", display: "flex", flexDirection: "column", gap: "16px" }}>
        {/* Contact details */}
        <div style={{ background: "#fff", borderRadius: "10px", padding: "16px", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: "6px" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#9e9e9e" }}>{isPerson ? "Person" : "Company"}</span>
          <span style={{ fontWeight: 700, fontSize: "0.97rem", color: "#212121" }}>{getContactName(entry)}</span>
          {contact?.position && <span style={{ fontSize: "0.82rem", color: "#757575" }}>{contact.position}</span>}
          {contact?.bemerkung && <span style={{ fontSize: "0.82rem", color: "#757575" }}>{contact.bemerkung}</span>}
        </div>

        {/* AI Research */}
        <AiResearchButton
          personId={isPerson ? contact?.id : undefined}
          companyId={!isPerson ? contact?.id : undefined}
        />

        {/* Score */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <label style={{ fontWeight: 600, fontSize: "0.88rem", color: "#424242" }}>Score</label>
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: sc.color, background: sc.bg, borderRadius: "12px", padding: "2px 10px" }}>{score}</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={score}
            onChange={(e) => setScore(Number(e.target.value))}
            style={{ width: "100%", accentColor: sc.color }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem", color: "#9e9e9e" }}>
            <span>0</span><span>50</span><span>100</span>
          </div>
        </div>

        {/* Reason */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontWeight: 600, fontSize: "0.88rem", color: "#424242" }}>Reason</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this contact in this pipeline?"
            rows={3}
            style={{ padding: "10px 14px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.88rem", outline: "none", background: "#fff", resize: "vertical", fontFamily: "inherit" }}
          />
        </div>

        {/* Move to step */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontWeight: 600, fontSize: "0.88rem", color: "#424242" }}>Move to column</label>
          <select
            value={stepId}
            onChange={(e) => setStepId(e.target.value)}
            style={{ padding: "10px 14px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.92rem", outline: "none", background: "#fff" }}
          >
            {steps.map(s => (
              <option key={s.id} value={s.id}>{s.name}{s.id === entry.step?.id ? " (current)" : ""}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ background: "#fff", borderTop: "1px solid #f0f0f0", padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", borderRadius: "0 0 14px 14px" }}>
        <button
          onClick={() => { onClose(); navigate(editPath); }}
          style={{ padding: "8px 16px", background: "#f0f4ff", color: "#1a73e8", border: "1px solid #c5d8fd", borderRadius: "8px", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: "0.78rem" }} />
          Edit Contact
        </button>
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", background: "#f5f5f5", color: "#424242", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", background: "#1a73e8", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Column = ({
  step, entries, projectId,
  onRename, onDelete, onAddEntry,
  onEntryDelete, onEntryEdit, onEntryDragStart,
  onColDragStart, onColDragOver, onColDrop,
  onEntryDragOver, onEntryDrop,
  isDragOver,
}) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(step.name);
  const inputRef = useRef(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commitRename = async () => {
    if (name.trim() && name !== step.name) await onRename(step, name.trim());
    setEditing(false);
  };

  return (
    <div
      draggable
      onDragStart={(e) => onColDragStart(e, step.id)}
      onDragOver={(e) => { e.preventDefault(); onColDragOver(e, step.id); }}
      onDrop={(e) => onColDrop(e, step.id)}
      style={{
        width: "280px",
        flexShrink: 0,
        background: isDragOver ? "#eef4ff" : "#f0f2f5",
        borderRadius: "14px",
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 220px)",
        border: isDragOver ? "2px dashed #1a73e8" : "2px solid transparent",
        transition: "border-color 0.15s, background 0.15s",
      }}
    >
      {/* Column header */}
      <div style={{
        padding: "14px 14px 10px",
        display: "flex", alignItems: "center", gap: "8px",
        cursor: "grab",
      }}>
        <FontAwesomeIcon icon={faGripVertical} style={{ color: "#ccc", fontSize: "0.8rem", flexShrink: 0 }} />
        {editing ? (
          <input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") { setName(step.name); setEditing(false); } }}
            style={{
              flex: 1, border: "1px solid #1a73e8", borderRadius: "6px",
              padding: "3px 8px", fontSize: "0.88rem", fontWeight: 700, outline: "none",
            }}
          />
        ) : (
          <span
            style={{ flex: 1, fontWeight: 700, fontSize: "0.88rem", color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            onDoubleClick={() => setEditing(true)}
          >
            {step.name}
          </span>
        )}
        <span style={{
          fontSize: "0.72rem", fontWeight: 700, color: "#9e9e9e",
          background: "#e0e0e0", borderRadius: "12px", padding: "1px 8px", flexShrink: 0,
        }}>
          {entries.length}
        </span>
        {editing ? (
          <>
            <button onClick={commitRename} style={{ background: "none", border: "none", color: "#2e7d32", cursor: "pointer", padding: "2px 4px" }}>
              <FontAwesomeIcon icon={faCheck} style={{ fontSize: "0.75rem" }} />
            </button>
            <button onClick={() => { setName(step.name); setEditing(false); }} style={{ background: "none", border: "none", color: "#c62828", cursor: "pointer", padding: "2px 4px" }}>
              <FontAwesomeIcon icon={faXmark} style={{ fontSize: "0.75rem" }} />
            </button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)} style={{ background: "none", border: "none", color: "#bdbdbd", cursor: "pointer", padding: "2px 4px" }}>
              <FontAwesomeIcon icon={faPencil} style={{ fontSize: "0.7rem" }} />
            </button>
            <button onClick={() => onDelete(step)} style={{ background: "none", border: "none", color: "#bdbdbd", cursor: "pointer", padding: "2px 4px" }}>
              <FontAwesomeIcon icon={faTrash} style={{ fontSize: "0.7rem" }} />
            </button>
          </>
        )}
      </div>

      {/* Cards */}
      <div
        onDragOver={(e) => { e.preventDefault(); onEntryDragOver(e, step.id); }}
        onDrop={(e) => onEntryDrop(e, step.id)}
        style={{
          flex: 1, overflowY: "auto", padding: "0 10px 10px",
          minHeight: "80px",
        }}
      >
        {entries.map((entry) => (
          <EntryCard
            key={entry.id}
            entry={entry}
            onDelete={onEntryDelete}
            onEdit={onEntryEdit}
            onDragStart={onEntryDragStart}
          />
        ))}
      </div>

      {/* Add card button */}
      <button
        onClick={() => onAddEntry(step.id)}
        style={{
          margin: "0 10px 12px",
          padding: "8px",
          border: "1px dashed #c8c8c8",
          borderRadius: "8px",
          background: "transparent",
          color: "#9e9e9e",
          cursor: "pointer",
          fontSize: "0.8rem",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          transition: "border-color 0.15s, color 0.15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#1a73e8"; e.currentTarget.style.color = "#1a73e8"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#c8c8c8"; e.currentTarget.style.color = "#9e9e9e"; }}
      >
        <FontAwesomeIcon icon={faPlus} /> Add card
      </button>
    </div>
  );
};

const Board = ({ project, reloadKey }) => {
  const [steps, setSteps] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedColId, setDraggedColId] = useState(null);
  const [draggedEntryId, setDraggedEntryId] = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);
  const [addingCol, setAddingCol] = useState(false);
  const [newColName, setNewColName] = useState("");
  const newColRef = useRef(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [pendingStepId, setPendingStepId] = useState(null);
  const [deleteColTarget, setDeleteColTarget] = useState(null);
  const [editEntry, setEditEntry] = useState(null);

  const load = async () => {
    setLoading(true);
    const [s, e] = await Promise.all([
      pipelineApi.getSteps(project.id),
      pipelineApi.getEntries(project.id),
    ]);
    setSteps(s);
    setEntries(e);
    setLoading(false);
  };

  useEffect(() => { load(); }, [project.id, reloadKey]);
  useEffect(() => { if (addingCol) newColRef.current?.focus(); }, [addingCol]);

  const entriesByStep = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      const sid = e.step?.id;
      if (sid === undefined || sid === null) return;
      if (!map[sid]) map[sid] = [];
      map[sid].push(e);
    });
    return map;
  }, [entries]);

  // ── Column CRUD ──────────────────────────────────────────────────────────
  const handleAddCol = async () => {
    if (!newColName.trim()) return;
    const res = await pipelineApi.createStep({ name: newColName.trim(), pipeline: { id: project.id } });
    if (res?.status === 200 || res?.status === 201) {
      const newStep = res.data;
      setSteps((prev) => [...prev, newStep]);
    }
    setNewColName("");
    setAddingCol(false);
  };

  const handleRenameCol = async (step, name) => {
    await pipelineApi.editStep({ id: step.id, name, pipeline: { id: project.id } });
    setSteps((prev) => prev.map((s) => (s.id === step.id ? { ...s, name } : s)));
  };

  const handleDeleteCol = async () => {
    if (!deleteColTarget) return;
    await pipelineApi.deleteStep(deleteColTarget.id);
    setSteps((prev) => prev.filter((s) => s.id !== deleteColTarget.id));
    setEntries((prev) => prev.filter((e) => e.step?.id !== deleteColTarget.id));
    setDeleteColTarget(null);
  };

  // ── Column DnD ────────────────────────────────────────────────────────────
  const handleColDragStart = (e, stepId) => {
    setDraggedColId(stepId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleColDragOver = (e, stepId) => {
    if (draggedColId && draggedColId !== stepId) setDragOverColId(stepId);
  };

  const handleColDrop = async (e, targetStepId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverColId(null);
    if (!draggedColId || draggedColId === targetStepId) { setDraggedColId(null); return; }

    const withoutDragged = steps.filter((s) => s.id !== draggedColId);
    const targetIdx = withoutDragged.findIndex((s) => s.id === targetStepId);
    const prevId = targetIdx > 0 ? withoutDragged[targetIdx - 1].id : 0;
    const nextId = targetStepId;

    const dragged = steps.find((s) => s.id === draggedColId);
    const reordered = [...withoutDragged];
    reordered.splice(targetIdx, 0, dragged);
    setSteps(reordered);
    setDraggedColId(null);

    await pipelineApi.reorderStep(draggedColId, prevId, nextId);
  };

  // ── Entry CRUD ────────────────────────────────────────────────────────────
  const handleAddEntry = (stepId) => {
    setPendingStepId(stepId);
    setShowCustomerModal(true);
  };

  const handleContactSelected = async (contact) => {
    setShowCustomerModal(false);
    if (!pendingStepId) return;

    const isPerson = contact.hasOwnProperty("anrede") || contact.hasOwnProperty("geschlecht");
    const payload = {
      pipeline: { id: project.id },
      step: { id: pendingStepId },
      ...(isPerson ? { person: { id: contact.id } } : { kunde: { id: contact.id } }),
    };

    const res = await pipelineApi.createEntry(payload);
    if (res?.status === 200 || res?.status === 201) {
      const newEntry = res.data;
      setEntries((prev) => [...prev, newEntry]);
    }
    setPendingStepId(null);
  };

  const handleDeleteEntry = async (entryId) => {
    await pipelineApi.deleteEntry(entryId);
    setEntries((prev) => prev.filter((e) => e.id !== entryId));
  };

  const handleEditSave = async (entry, newStepId, score, reason) => {
    const payload = {
      id: entry.id,
      pipeline: { id: project.id },
      step: { id: newStepId },
      score,
      reason,
      ...(entry.kunde  ? { kunde:  { id: entry.kunde.id  } } : {}),
      ...(entry.person ? { person: { id: entry.person.id } } : {}),
    };
    await pipelineApi.editEntry(payload);
    setEntries((prev) => prev.map((e) => e.id === entry.id ? { ...e, step: { id: newStepId }, score, reason } : e));
  };

  // ── Entry DnD ─────────────────────────────────────────────────────────────
  const handleEntryDragStart = (e, entryId) => {
    setDraggedEntryId(entryId);
    e.dataTransfer.effectAllowed = "move";
    e.stopPropagation();
  };

  const handleEntryDragOver = (e, stepId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverColId(stepId);
  };

  const handleEntryDrop = async (e, stepId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverColId(null);
    if (!draggedEntryId) return;

    const entry = entries.find((en) => en.id === draggedEntryId);
    if (!entry || entry.step?.id === stepId) { setDraggedEntryId(null); return; }

    const payload = {
      id: entry.id,
      pipeline: { id: project.id },
      step: { id: stepId },
      ...(entry.kunde ? { kunde: { id: entry.kunde.id } } : {}),
      ...(entry.person ? { person: { id: entry.person.id } } : {}),
    };

    setEntries((prev) => prev.map((en) =>
      en.id === draggedEntryId ? { ...en, step: { id: stepId } } : en
    ));
    setDraggedEntryId(null);

    await pipelineApi.editEntry(payload);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, minHeight: "300px", gap: "12px", color: "#9e9e9e" }}>
        <div style={{ width: "32px", height: "32px", borderRadius: "50%", border: "3px solid #e8f0fe", borderTopColor: "#1a73e8", animation: "spin 0.8s linear infinite" }} />
        <span style={{ fontSize: "0.88rem", fontWeight: 500 }}>Loading board…</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
      <style>{`.modal-pipeline-content { background: transparent !important; border: none !important; box-shadow: none !important; }`}</style>
      <CustomerModal show={showCustomerModal} setShow={setShowCustomerModal} updateContact={handleContactSelected} />
      {editEntry && (
        <EntryEditModal
          entry={editEntry}
          steps={steps}
          onClose={() => setEditEntry(null)}
          onSave={handleEditSave}
        />
      )}

      {/* Delete column confirm modal */}
      <Modal show={!!deleteColTarget} onHide={() => setDeleteColTarget(null)} centered size="sm" contentClassName="modal-pipeline-content">
        <div style={{ background: "linear-gradient(135deg, #c62828 0%, #b71c1c 100%)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: "14px 14px 0 0" }}>
          <span style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>Delete Column</span>
          <button onClick={() => setDeleteColTarget(null)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px", width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "1rem" }}>✕</button>
        </div>
        <div style={{ padding: "24px", background: "#f8f9fb", fontSize: "0.92rem", color: "#424242" }}>
          Delete column <strong>"{deleteColTarget?.name}"</strong> and all its cards?
        </div>
        <div style={{ background: "#fff", borderTop: "1px solid #f0f0f0", padding: "14px 24px", display: "flex", justifyContent: "flex-end", gap: "10px", borderRadius: "0 0 14px 14px" }}>
          <button onClick={() => setDeleteColTarget(null)} style={{ padding: "8px 18px", background: "#f5f5f5", color: "#424242", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleDeleteCol} style={{ padding: "8px 20px", background: "#c62828", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer" }}>Delete</button>
        </div>
      </Modal>

      <section className="content">
        <div style={{
          background: "#fff",
          borderRadius: "14px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          border: "1px solid #f0f0f0",
          padding: "20px 24px",
          overflowX: "auto",
        }}>
          <div
            style={{ display: "flex", gap: "14px", paddingBottom: "8px", alignItems: "flex-start", minWidth: "min-content" }}
            onDragEnd={() => { setDraggedColId(null); setDraggedEntryId(null); setDragOverColId(null); }}
          >
            {steps.map((step) => (
              <Column
                key={step.id}
                step={step}
                entries={entriesByStep[step.id] || []}
                projectId={project.id}
                onRename={handleRenameCol}
                onDelete={(step) => setDeleteColTarget(step)}
                onAddEntry={handleAddEntry}
                onEntryDelete={handleDeleteEntry}
                onEntryEdit={setEditEntry}
                onEntryDragStart={handleEntryDragStart}
                onColDragStart={handleColDragStart}
                onColDragOver={handleColDragOver}
                onColDrop={handleColDrop}
                onEntryDragOver={handleEntryDragOver}
                onEntryDrop={handleEntryDrop}
                isDragOver={dragOverColId === step.id}
              />
            ))}

            {/* Add column */}
            <div style={{ width: "260px", flexShrink: 0 }}>
              {addingCol ? (
                <div style={{ background: "#f0f2f5", borderRadius: "14px", padding: "14px" }}>
                  <input
                    ref={newColRef}
                    value={newColName}
                    onChange={(e) => setNewColName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAddCol(); if (e.key === "Escape") { setAddingCol(false); setNewColName(""); } }}
                    placeholder="Column name…"
                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #1a73e8", borderRadius: "8px", fontSize: "0.88rem", outline: "none", marginBottom: "10px" }}
                  />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={handleAddCol} style={{ flex: 1, padding: "7px", borderRadius: "8px", background: "#1a73e8", border: "none", color: "#fff", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer" }}>Add</button>
                    <button onClick={() => { setAddingCol(false); setNewColName(""); }} style={{ flex: 1, padding: "7px", borderRadius: "8px", background: "#f5f5f5", border: "none", color: "#616161", fontWeight: 600, fontSize: "0.82rem", cursor: "pointer" }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setAddingCol(true)}
                  style={{ width: "100%", padding: "14px", borderRadius: "14px", border: "2px dashed #d0d0d0", background: "transparent", color: "#9e9e9e", cursor: "pointer", fontWeight: 700, fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#1a73e8"; e.currentTarget.style.color = "#1a73e8"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#d0d0d0"; e.currentTarget.style.color = "#9e9e9e"; }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add column
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Board;
